import React, { useState, useEffect, useRef } from 'react';
import { getAssistantStatus, getAssistantConversation, getAssistantMessages, sendAssistantMessage, resetAssistantSession } from './api';
import './Helpdesk.css';

function Helpdesk({ isOpen, onClose }) {
  const [enabled, setEnabled] = useState(false);
  const [statusResponse, setStatusResponse] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      loadAssistant();
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadAssistant = async () => {
    try {
      setLoading(true);

      // Verificar si está habilitado
      const response = await getAssistantStatus();
      const { enabled, missingApiKey } = response.data;

      console.log('[Helpdesk] Status:', { enabled, missingApiKey });

      setStatusResponse(response);
      setEnabled(enabled);

      if (enabled) {
        // Obtener o crear conversación
        const convResponse = await getAssistantConversation();
        setConversation(convResponse.data);

        // Cargar mensajes existentes
        const messagesResponse = await getAssistantMessages(convResponse.data.id);
        const msgs = messagesResponse.data.messages || [];

        console.log('[Helpdesk] Mensajes cargados:', msgs.length);

        setMessages(msgs);

        // Si no hay mensajes, enviar saludo automático
        if (msgs.length === 0) {
          console.log('[Helpdesk] Sin mensajes, enviando saludo automático...');
          setTimeout(() => {
            sendGreeting(convResponse.data.id);
          }, 500);
        }
      } else if (missingApiKey) {
        console.log('[Helpdesk] Asistente deshabilitado: falta API key');
      }
    } catch (error) {
      console.error('[Helpdesk] Error cargando asistente:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || sending) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setSending(true);

    // Agregar mensaje del usuario a la UI
    const newUserMessage = {
      role: 'user',
      content: userMessage,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, newUserMessage]);

    try {
      const response = await sendAssistantMessage(conversation.id, userMessage);

      if (response.data.success) {
        // Agregar respuesta del asistente
        const assistantMessage = {
          role: 'assistant',
          content: response.data.message,
          created_at: new Date().toISOString(),
          cached: response.data.cached,
          easter_egg: response.data.easter_egg
        };
        setMessages(prev => [...prev, assistantMessage]);
      }
    } catch (error) {
      console.error('[Helpdesk] Error enviando mensaje:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Error al procesar tu mensaje. Inténtalo de nuevo.',
        created_at: new Date().toISOString(),
        error: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setSending(false);
    }
  };

  const sendGreeting = async (conversationId) => {
    setSending(true);

    try {
      // Enviar mensaje especial para activar saludo
      const response = await sendAssistantMessage(conversationId, '👋');

      if (response.data.success) {
        const assistantMessage = {
          role: 'assistant',
          content: response.data.message,
          created_at: new Date().toISOString(),
          cached: response.data.cached,
          easter_egg: response.data.easter_egg
        };
        setMessages([assistantMessage]);
      }
    } catch (error) {
      console.error('[Helpdesk] Error enviando saludo:', error);
    } finally {
      setSending(false);
    }
  };

  const handleResetSession = async () => {
    if (!conversation) return;

    const confirmed = window.confirm('¿Reiniciar la conversación? Se archivará el historial actual.');
    if (!confirmed) return;

    try {
      console.log('[Helpdesk] Reiniciando sesión...');
      await resetAssistantSession(conversation.id);

      // Limpiar mensajes del estado
      setMessages([]);

      console.log('[Helpdesk] Sesión reiniciada, enviando saludo automático...');

      // Enviar saludo automático
      setTimeout(() => {
        sendGreeting(conversation.id);
      }, 500);
    } catch (error) {
      console.error('[Helpdesk] Error reiniciando sesión:', error);
      alert('Error al reiniciar la conversación. Inténtalo de nuevo.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="helpdesk-overlay" onClick={onClose}>
      <div className="helpdesk-container" onClick={(e) => e.stopPropagation()}>
        <div className="helpdesk-header">
          <div className="helpdesk-header-content">
            <img
              src="/images/hellbot.png?v=2.13.2"
              alt="Helpdesk"
              className="helpdesk-avatar-header"
              onClick={() => setShowAvatarModal(true)}
              title="Ver imagen completa"
            />
            <div className="helpdesk-title">
              <h2>HellBot</h2>
              <span className="helpdesk-subtitle">
                {enabled ? 'Asistente DevOps' : 'Modo ahorro de energía'}
              </span>
            </div>
          </div>
          <div className="helpdesk-header-actions">
            {enabled && conversation && (
              <button
                className="helpdesk-reset"
                onClick={handleResetSession}
                title="Reiniciar sesión"
              >
                🔄
              </button>
            )}
            <button className="helpdesk-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {loading ? (
          <div className="helpdesk-loading">
            <div className="helpdesk-spinner"></div>
            <p>Activando HellBot...</p>
          </div>
        ) : !enabled ? (
          <div className="helpdesk-disabled">
            <img
              src="/images/hellbot.png?v=2.13.2"
              alt="HellBot en modo ahorro"
              className="helpdesk-avatar-sleeping"
            />
            <h3>HellBot está en modo ahorro de energía en este momento</h3>
            <p>
              {statusResponse?.data?.missingApiKey
                ? 'Se requiere configuración de API key por un administrador.'
                : 'Un administrador debe activar el asistente.'}
            </p>
          </div>
        ) : (
          <>
            <div className="helpdesk-messages">
              {messages.map((msg, index) => (
                <div key={index} className={`helpdesk-message ${msg.role}`}>
                  {msg.role === 'assistant' && (
                    <img
                      src="/images/hellbot.png?v=2.13.2"
                      alt="Helpdesk"
                      className="helpdesk-avatar-message"
                    />
                  )}
                  <div className="helpdesk-message-content">
                    <div className="helpdesk-message-text">
                      {msg.content}
                      {msg.cached && (
                        <span className="helpdesk-badge cached" title="Respuesta desde conocimiento aprendido">
                          🧠 Cached
                        </span>
                      )}
                      {msg.easter_egg && (
                        <span className="helpdesk-badge easter-egg" title="Easter egg activado">
                          🎭
                        </span>
                      )}
                    </div>
                    <div className="helpdesk-message-time">
                      {new Date(msg.created_at).toLocaleTimeString('es-ES', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                </div>
              ))}
              {sending && (
                <div className="helpdesk-message assistant typing">
                  <img
                    src="/images/hellbot.png?v=2.13.2"
                    alt="Helpdesk"
                    className="helpdesk-avatar-message"
                  />
                  <div className="helpdesk-message-content">
                    <div className="helpdesk-typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <form className="helpdesk-input-container" onSubmit={handleSendMessage}>
              <input
                type="text"
                className="helpdesk-input"
                placeholder="Escribe tu pregunta..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                disabled={sending}
              />
              <button
                type="submit"
                className="helpdesk-send-button"
                disabled={!inputMessage.trim() || sending}
              >
                {sending ? '⏳' : '➤'}
              </button>
            </form>
          </>
        )}
      </div>

      {/* Modal para ver imagen completa del avatar */}
      {showAvatarModal && (
        <div className="helpdesk-avatar-modal" onClick={() => setShowAvatarModal(false)}>
          <div className="helpdesk-avatar-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="helpdesk-avatar-modal-close" onClick={() => setShowAvatarModal(false)}>✕</button>
            <img src="/images/hellbot.png?v=2.13.2" alt="HellBot Avatar" />
            <p className="helpdesk-avatar-modal-text">HellBot - Asistente DevOps de CloudAppi</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Helpdesk;
